import { useState, useEffect } from 'react';
import api from '../../services/api';

export default function AddTeachersToBatchModal({ isOpen, onClose, batchId, centerId, existingTeacherIds = [], onSuccess }) {
  const [centerTeachers, setCenterTeachers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen || !centerId) return;
    setLoading(true);
    api.getCoachingCenter(centerId)
      .then(r => {
        const teachers = (r.data.teachers || [])
          .filter(t => t.is_active !== false)
          .filter(t => !existingTeacherIds.includes(t.id));
        setCenterTeachers(teachers);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [isOpen, centerId, existingTeacherIds.length]);

  const toggle = (tid) => {
    setSelected(s => s.includes(tid) ? s.filter(x => x !== tid) : [...s, tid]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selected.length === 0) {
      setError('Select at least one teacher');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await api.addTeachersToBatch(batchId, selected);
      onSuccess();
      onClose();
      setSelected([]);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col">
        <div className="p-6 border-b border-teacher-border">
          <h2 className="text-xl font-semibold text-teacher-text-primary">Add Teachers to Batch</h2>
          <p className="text-sm text-teacher-text-secondary mt-1">
            Only teachers in this batch's coaching center are eligible. Teachers already in this batch are hidden.
          </p>
        </div>
        <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
          <div className="p-6 space-y-4 flex-1 overflow-auto">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">{error}</div>
            )}
            {loading ? (
              <div className="text-center py-8 text-teacher-text-secondary">Loading available teachers...</div>
            ) : centerTeachers.length === 0 ? (
              <div className="text-center py-8 text-teacher-text-secondary">No additional active teachers in this center.</div>
            ) : (
              <div className="space-y-2">
                <div className="text-sm text-teacher-text-secondary mb-2">{selected.length} selected</div>
                {centerTeachers.map(t => (
                  <label key={t.id} className="flex items-center gap-3 p-3 border border-teacher-border rounded-lg hover:bg-teacher-bg cursor-pointer">
                    <input type="checkbox" checked={selected.includes(t.id)} onChange={() => toggle(t.id)}
                      className="w-4 h-4 text-primary-600 rounded" />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-teacher-text-primary">{t.first_name} {t.last_name}</div>
                      <div className="text-xs text-teacher-text-secondary">{t.email} · {(t.subjects || []).join(', ') || 'all subjects'}</div>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>
          <div className="p-6 border-t border-teacher-border flex gap-3">
            <button type="button" onClick={() => { onClose(); setSelected([]); }}
              disabled={submitting}
              className="flex-1 px-4 py-2 border border-teacher-border-strong rounded-lg text-teacher-text-primary hover:bg-teacher-bg">
              Cancel
            </button>
            <button type="submit" disabled={submitting || selected.length === 0}
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50">
              {submitting ? 'Adding...' : `Add ${selected.length} Teacher(s)`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
